import moment from 'moment';
import { httpClient } from './index';
import { convertFullCalendarDateRangeToParams } from '../../util/dateTransformations';
import { specialties as staticUrls } from '../../components/ProviderEditModal/data';

// Appointments

export const fetchAppointments = async ({
  providerId,
  upcoming,
  past,
  offset,
}) => {
  const result = await httpClient.get(
    `/api/admin/appointments/${providerId}?upcoming=${upcoming}&past=${past}&offset=${offset}&limit=${20}`
  );
  const appointments = result.data || [];
  return appointments.map(appointment => ({
    id: appointment.id,
    time: appointment.time,
    portalNotes: appointment.portalNotes,
    status: appointment.status,
    patientId: appointment.patient.id,
    patientName: appointment.patient.name,
    patientReason: appointment.patientReason,
    patientAge: appointment.patient.age,
    patientGender: appointment.patient.gender,
    providerId: appointment.provider.id,
    providerName: appointment.provider.name,
    tenancyId: appointment.tenancy.id,
    tenancyLocation: appointment.tenancy.location,
  }));
};

export const cancelAppointmentFromAdmin = async (
  appointmentId,
  cancelReason,
  reasonOfCancelAppt
) => {
  return await httpClient.post('/api/admin/appointments/cancel', {
    appointmentId,
    cancelReason,
    reasonOfCancelAppt,
  });
};

export const fetchAppointmentStatuses = async () => {
  const results = await httpClient.get('/api/admin/appointments/statuses');
  return results.data || [];
};

export const updateAppointmentStatus = async (appointment, tabPosition) => {
  return httpClient.put(`/api/admin/appointments/${appointment.id}`, {
    status: appointment.status,
    tabPosition,
  });
};

// Availability

export const fetchAdminAvailability = async ({ provider, dateRange, tenancyType, zipcode }) => {
  const params = {
    providerId: provider.id,
    range: convertFullCalendarDateRangeToParams(dateRange),
    tenancyType,
    zipcode
  };
  const result = await httpClient.post(`/api/admin/availability/fetch`, params);
  // console.log('result.Event????>>>>', result[0].start);
  const results = result.data.map(event => ({
    start: moment.utc(event.start).toISOString(),
    end: moment.utc(event.end).toISOString(),
    // start: moment(event.start).format(),
    // end: moment(event.end).format(),
    availability: true,
    tenancyId: event.tenancy.id,
    tenancyColor: event.tenancy.color,
    providerId: provider.id,
    scheduleId: event.scheduleId
  }));
  return results;
};

export const createAvailability = async ({
  start,
  end,
  providerId,
  tenancyId,
  telemedicineId,
  scheduleId,
}) => {
  const availabilityDetails = {
    startTimestamp: start,
    endTimestamp: end,
    provider: {
      id: providerId,
    },
    tenancy: {
      id: tenancyId,
    },
    telemedicineId,
    scheduleId,
  };

  return httpClient.post('/api/admin/availability', availabilityDetails);
};

export const deleteAvailability = async ({
  start,
  end,
  providerId,
  tenancyId,
  scheduleId
}) => {
  const availabilityDetails = {
    startTimestamp: start,
    endTimestamp: end,
    provider: {
      id: providerId,
    },
    tenancy: {
      id: tenancyId,
    },
    scheduleId
  };

  return httpClient.post('/api/admin/availability/delete', availabilityDetails);
};

// For Providers, see api/index.js

// Patients
export const importPatients = async params => {
  return httpClient.post('/api/admin/patients/import', params);
};

// Appointment Requests

export const createAppointmentRequest = async ({
  patient,
  provider,
  appointment,
}) => {
  const appointmentDetails = {
    patient: { id: patient.id },
    provider: { id: provider.id },
    appointment: {
      portalNotes: appointment.portalNotes,
      createdBy: appointment.createdBy,
    },
  };

  return httpClient.post('/api/admin/appointment-requests', appointmentDetails);
};

export const fetchAppointmentRequests = async ({
  providerId,
  archived,
  fromProviders,
  fromSearch,
  offset,
}) => {
  const appointmentRequests = await httpClient.get(
    `/api/admin/appointment-requests/${providerId}?archived=${archived}&fromProviders=${fromProviders}&fromSearch=${fromSearch}&offset=${offset}&limit=${20}`
  );
  return appointmentRequests.data.map(request => ({
    id: request.id,
    patient: {
      firstName: request.patient.firstName,
      lastName: request.patient.lastName,
      name: request.patient.name,
      email: request.patient.email,
      phone: request.patient.phone,
      birthdate: moment.utc(request.patient.birthdate).format('MM/DD/YYYY'),
      insuranceId: request.patient.insuranceId,
      insuranceName: request.patient.insuranceName,
      payerId: request.patient.payerId,
      groupNumber: request.patient.groupNumber,
      addressLine1: request.patient.addressLine1,
      addressLine2: request.patient.addressLine2,
      zipcode: request.patient.zipcode,
      city: request.patient.city,
      state: request.patient.state,
    },
    sender: {
      name: request.sender.name,
      phone: request.sender.phone,
      practiceName: '',
    },
    receiver: {
      name: request.receiver.name,
      phone: request.receiver.phone,
    },
    portalNotes: request.portalNotes,
    visitReason: request.visitReason,
    createdAt: request.createdAt,
  }));
};

export const fetchAppointmentRequestsFromWebsite = async ({
  premium = false,
  freemium = false,
  archived = false,
  offset,
}) => {
  const AppointmentRequests = await httpClient.get(
    `/api/admin/appointment-requests/from-website?freemium=${freemium}&premium=${premium}&archived=${archived}&offset=${offset}&limit=${20}`
  );

  return AppointmentRequests.data.map(request => ({
    id: request.id,
    providerName: request.provider.name,
    firstName: request.firstName,
    lastName: request.lastName,
    name: request.name,
    email: request.email,
    phone: request.phone,
    birthdate: moment.utc(request.birthdate).format('MM/DD/YYYY'),
    insuranceId: request.insuranceId,
    insuranceName: request.insuranceName,
    visitReason: request.visitReason,
    archived: request.archived,
    createdAt: request.createdAt,
    portalNotes: request.portalNotes,
    payerId: request.payerId,
    groupNumber: request.groupNumber,
    city: request.city,
    state: request.state,
  }));
};

export const archiveAppointmentRequest = async id => {
  return httpClient.put(`/api/admin/appointment-requests/archive/`, { id });
};

export const updateAppointmentRequest = async appointmentRequest => {
  return httpClient.put(
    `/api/admin/appointment-requests/notes/${appointmentRequest.id}`,
    {
      portalNotes: appointmentRequest.portalNotes,
    }
  );
};

// Specialties

export const fetchSpecialties = async () => {
  const result = await httpClient.get('/api/admin/specialties');
  const specialties = result.data || [];
  return specialties.map(specialty => ({
    id: specialty.id,
    name: specialty.name,
    shortName: specialty.shortName,
  }));
};

export const createSpecialty = async params => {
  return httpClient.post('/api/admin/specialties', params);
};

export const updateSpecialty = async params => {
  return httpClient.put(`/api/admin/specialties/${params.id}`, {
    name: params.name,
  });
};

// Insurances

export const fetchInsurances = async () => {
  const result = await httpClient.get('/api/admin/insurances');
  const insurances = result.data || [];
  return insurances.map(insurance => ({
    id: insurance.id,
    name: insurance.name,
    shortName: insurance.shortName,
  }));
};

export const createInsurance = async params => {
  return httpClient.post('/api/admin/insurances', params);
};

export const updateInsurance = async params => {
  return httpClient.put(`/api/admin/insurances/${params.id}`, {
    name: params.name,
  });
};

// Practices
export const fetchPracticesByName = async practiceName => {
  const result = await httpClient.get(
    `/api/admin/practices/name/${practiceName}`
  );
  return result.data.map(mapPracticeToStdDs);
};

export const fetchPractices = async () => {
  const result = await httpClient.get('/api/admin/practices');
  const practices = result.data || [];
  return practices.map(practice => ({
    id: practice.id,
    name: practice.name,
    catchAllEmail: practice.catchAllEmail,
    fax: practice.fax,
    notificationPreference: practice.notificationPreference,
    providers: (practice.providers || []).map(provider => ({
      id: provider.id,
      name: provider.name,
    })),
  }));
};

export const createPractice = async params => {
  return httpClient.post('/api/admin/practices', {
    name: params.name,
    catchAllEmail: params.catchAllEmail
  });
};

export const updatePractice = async params => {
  const result = await httpClient.put(`/api/admin/practices/${params.id}`, {
    name: params.name,
    catchAllEmail: params.catchAllEmail
  });

  const practice = result.data || {};
  const { id, name, catchAllEmail, providers } = practice;

  return {
    id,
    name,
    catchAllEmail,
    providers: (providers || []).map(provider => ({
      id: provider.id,
      firstName: provider.firstName,
      lastName: provider.lastName,
      name: provider.name,
      tenancies: (provider.tenancies || [])
        .filter(tenancy => tenancy.active)
        .map(tenancy => ({
          id: tenancy.id,
          location: tenancy.location,
        })),
    })),
  };
};

const mapPracticeToStdDs = practice => {
  return {
    id: practice.id,
    name: practice.name,
    catch_all_email: practice.catch_all_email,
  };
};

// Tenancies
const mapTenancyToStdDs = tenancy => ({
  id: tenancy.id,
  name: tenancy.location,
  locationShortName: tenancy.locationShortName,
  color: tenancy.color,
  active: tenancy.active,
  aptDurationMinutes: tenancy.aptDurationMinutes,
  zipcode: tenancy.zipcode,
});

export const fetchTenanciesByLocation = async tenancyLocation => {
  const result = await httpClient.get(
    `/api/admin/tenancies/tenancy/${tenancyLocation}`
  );
  return result.data.map(mapTenancyToStdDs);
};

export const fetchTenancies = async providerId => {
  const result = await httpClient.get(`/api/admin/tenancies/${providerId}`);
  return result.data.map(tenancy => ({
    id: tenancy.id,
    location: tenancy.location,
    locationShortName: tenancy.locationShortName,
    color: tenancy.color,
    active: tenancy.active,
    apptDurationMinutes: tenancy.apptDurationMinutes,
    zipcode: tenancy.zipcode,
    virtualRoomUrl: tenancy.virtualRoomUrl,
  }));
};

export const createTenancy = async tenancy => {
  return httpClient.post(`/api/admin/tenancies`, tenancy);
};

export const updateTenancy = async tenancy => {
  return httpClient.put(`/api/admin/tenancies/${tenancy.id}`, tenancy);
};

export const checkAffiliateLinkExists = async tenancyId => {
  const result = await httpClient.get(
    `/api/admin/tenancies/verify/${tenancyId}`
  );
  return {
    exists: result.data.exists,
  };
};

// Administrators

export const fetchAdministrators = async () => {
  const result = await httpClient.get('/api/admin/administrators');
  return (result.data || []).map(administrator => ({
    id: administrator.id,
    userId: administrator.userId,
    name: administrator.name,
    firstName: administrator.firstName,
    lastName: administrator.lastName,
    email: administrator.email,
    sendMail: administrator.sendMail,
    fax: administrator.fax,
    notificationPreference: administrator.notificationPreference
  }));
};

export const createAdministrator = async params => {
  return httpClient.post('/api/admin/administrators', params);
};

export const updateAdministrator = async params => {
  return httpClient.put(`/api/admin/administrators/${params.id}`, params);
};

export const deleteAdministrator = async params => {
  return httpClient.delete(`/api/admin/administrators/${params.id}`);
};
 
export const changeMailStatusAdministrator = async params => {
  return httpClient.post(`/api/admin/administrators/mail-status/${params.id}`);
};

// Masquerade

export const masqueradeAs = async userId => {
  return httpClient.post('/api/admin/masquerade', { userId });
};

export const deleteMasquerade = async () => {
  return httpClient.delete('/api/admin/masquerade');
};

// Cities

export const fetchAllCities = async () => {
  const result = await httpClient.get(`/api/admin/cities`);
  return (result.data || []).map(city => ({
    id: city.id,
    name: city.name,
    shortName: city.shortName,
    zipcodes: city.zipcodes || [],
  }));
};

export const createCity = async params => {
  return httpClient.post('/api/admin/cities', params);
};

export const updateCity = async params => {
  return httpClient.put(`/api/admin/cities/${params.id}`, params);
};

// Reviews

export const fetchReviews = async () => {
  const result = await httpClient.get(`/api/admin/reviews`);
  return (result.data || []).map(review => ({
    id: review.id,
    providerId: review.providerId,
    providerName: review.providerName,
    rating: review.rating,
    description: review.description,
  }));
};

export const createReview = async params => {
  return httpClient.post('/api/admin/reviews', params);
};

export const updateReview = async params => {
  return httpClient.put(`/api/admin/reviews/${params.id}`, params);
};

export const deleteReview = async review => {
  return httpClient.delete(`/api/admin/reviews/${review.id}`);
};

// Recurring Availability

export const fetchRecurringAvailability = async () => {
  const { data } = await httpClient.get(`/api/admin/recurring-availability`);
  return data;
};

export const fetchRecurringAvailabiltyByProviderId = async providerId => {
  const { data } = await httpClient.get(
    `/api/admin/recurring-availability/${providerId}`
  );
  return data;
};

export const createRecurringAvailability = async recurringAvailability => {
  const params = {
    tenancyId: recurringAvailability.tenancyId,
    telemedicineId: recurringAvailability.telemedicineId,
    dayOfWeek: recurringAvailability.dayOfWeek,
    startsOn: recurringAvailability.startsOn,
    start: recurringAvailability.start,
    end: recurringAvailability.end,
    durationWeeks: recurringAvailability.durationWeeks,
    durationWeekDates: recurringAvailability.durationDayArray,
  };
  return httpClient.post('/api/admin/recurring-availability', params);
};

export const deleteRecurringAvailability = async recurringAvailability => {
  return httpClient.delete(
    `/api/admin/recurring-availability/${recurringAvailability.id}`
  );
};

//Appointment Duration
/*
newAptRules data struture to be sent to api
[
  {
    priorityLevel: one of ['practiceVisitType', 'providerVisitType', 'tenancyVisitType'],
    uuid: UUID, // (this is the uuid to join to a row in another table)
    durationMinutes: int,
    visitType: str
  },
  ...
]
*/
export const createAptDurationRules = async newAptRules => {
  return httpClient.post('/api/admin/appointment-duration-rules', newAptRules);
  // note to self probbaly return something to component to confirm rules got through correctly?
};

//Provider Pages Contents

export const fetchContentsByProviderId = async providerId => {
  const result = await httpClient.get(
    `/api/provider-pages-contents/provider/${providerId}`
  );
  return result.data;
};

export const updateProfileContent = async content => {
  const result = httpClient.put(
    `/api/provider-pages-contents/${content.id}`,
    content
  );
  return result.data;
};

export const createProfileContent = async newContent => {
  const result = httpClient.post('/api/provider-pages-contents', newContent);
  return result.data;
};

export const deleteProfileContent = async contentId => {
  return httpClient.delete(`/api/provider-pages-contents/${contentId}`);
};

//FAQs
export const createFAQreq = async newFAQ => {
  const result = await httpClient.post(
    '/api/provider-pages-contents/faq',
    newFAQ
  );
  return result.data;
};

export const updateFAQreq = async FAQ => {
  const result = await httpClient.put(`/api/provider-pages-contents/faq`, FAQ);
  return result.data;
};

export const deleteFAQreq = async FAQid => {
  const result = await httpClient.delete(
    `/api/provider-pages-contents/faq/${FAQid}`
  );
  return result.data;
};

export const getFAQsReq = async providerId => {
  const result = await httpClient.get(
    `/api/provider-pages-contents/provider-faqs/${providerId}`
  );
  return result.data;
};

export const getFAQbyIdReq = async faqId => {
  const result = await httpClient.get(
    `/api/provider-pages-contents/faq/${faqId}`
  );
  return result.data;
};

//Blog Content
export const fetchBlogContents = async () => {
  const result = await httpClient.get('/api/blog-contents/all');
  return result.data;
};
export const fetchBlogContentById = async id => {
  const result = await httpClient.get(`/api/blog-contents/${id}`);
  return result.data;
};

export const updateBlogContent = async content => {
  const result = await httpClient.put(
    `/api/blog-contents/${content.id}`,
    content
  );
  return result.data;
};

export const createBlogContent = async newContent => {
  const result = await httpClient.post('/api/blog-contents', newContent);
  return result.data;
};

export const deleteBlogContent = async contentId => {
  return httpClient.delete(`/api/blog-contents/${contentId}`);
};

//Press Content
export const fetchPressContents = async () => {
  const result = await httpClient.get('/api/press-contents/all');
  return result.data;
};

export const fetchPressContentById = async id => {
  const result = await httpClient.get(`/api/press-contents/${id}`);
  return result.data;
};

export const updatePressContent = async content => {
  const result = await httpClient.put(
    `/api/press-contents/${content.id}`,
    content
  );
  return result.data;
};

export const createPressContent = async newContent => {
  const result = await httpClient.post('/api/press-contents', newContent);
  return result.data;
};

export const deletePressContent = async contentId => {
  return httpClient.delete(`/api/press-contents/${contentId}`);
};

//Group-Patient API'S
export const createPatientGroup = async groupBody => {
  return await httpClient.post('/api/group-patient/creategroup', groupBody);
};

export const checkProviderRestrictedMode = async providerId => {
  return await httpClient.put('/api/group-patient/checkProviderMode', {
    providerId,
  });
};
export const checkProviderEligibleMode = async (providerId,patientId) => {
  return await httpClient.post('/api/appointments/checkEligible', {
    providerId,
    patientId
  });
};
export const checkProviderEligible = async (birthdate,gender,providerId) => {
  return await httpClient.post('/api/appointments/checkEligible', {
    birthdate,
    gender,
    providerId
  });
};

export const providerRestrictedByAdmin = async id => {
  return await httpClient.put('/api/group-patient/restricted', { id });
};

export const getGroupPatients = async ({ offset, limit }) => {
  return await httpClient.get(
    `/api/group-patient?offset=${offset}&limit=${limit}`
  );
};

export const checkBothPatientAndProviderId = async (patientId, providerId) => {
  return await httpClient.get(
    `/api/group-patient/checkPatient?patientId=${patientId}&providerId=${providerId}`
  );
};

export const removePatientFromGroup = async id => {
  return await httpClient.put('/api/group-patient/removePatientInGroup', {
    id,
  });
};

export const removeProviderInRestrictMode = async providerId => {
  return await httpClient.put('/api/group-patient/removeProviderRestrict', {
    providerId,
  });
};

export const getAllRestrictedProviders = async (
  practiceId,
  limit,
  offset,
  providerId
) => {
  return await httpClient.put('/api/providers/allProviders', {
    practiceId,
    limit,
    offset,
    providerId,
  });
};

export const fetchProviderGroupMembers = async (
  providerId,
  limit,
  offset,
  patientId
) => {
  return await httpClient.get(
    `/api/group-patient/fetchProviderMembers?providerId=${providerId}&limit=${limit}&offset=${offset}&patientId=${patientId}`
  );
};

export const fetchProviderDetails = async providerId => {
  return await httpClient.get(`/api/providers/providerDetails/${providerId}`);
};

export const fetchAllOneTimePayments = async (offset, limit) => {
  const result = await httpClient.get(
    `/api/payment/subscription/oneTimePayment?offset=${offset}&limit=${limit}`
  );
  return result;
};

export const fetchSessionCheckout = async queryString => {
  const result = await httpClient.get(
    `/api/payment/retriveSession/checkout?id=${queryString}`
  );
  return result;
};

export const fetchAllSubPatients = async (paymentId, limit, offset) => {
  const result = await httpClient.put(
    '/api/payment/subscription/getSubPatients',
    {
      paymentId,
      limit,
      offset,
    }
  );
  return result;
};

export const fetchAllProvidedUrls = async () => {
  let providedUrls = [];
  const predefinedUrls = Array.isArray(staticUrls)
    ? staticUrls.map(({ name, shortName }) => ({ name, shortName }))
    : [];

  let dynamicUrls = [];
  try {
    dynamicUrls = await fetchSpecialties();
    dynamicUrls = dynamicUrls.map(({ id, ...rest }) => rest);
  } catch (err) {
    console.log("error");
  }

  // Merge and de-duplicate
  const urls = [
    ...predefinedUrls,
    ...dynamicUrls.filter(item2 => !predefinedUrls.some(item1 => item1.shortName.trim().toLowerCase() === item2.shortName.trim().toLowerCase()))
];
  
providedUrls = urls.sort((a, b) => a.name.localeCompare(b.name));
  return providedUrls;
};

export const fetchTebraInsurancesList = async () => {
  const result = await httpClient.get(`/api/admin/tebra-insurances`);
  return result;
};